import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { TableData } from '../types/api/tables';
import { QuickAccessTable, toMemoryOptimized } from '../utils/tableAccess';
import { mergeAdminSegmentTableData } from '../utils/adminSegmentMerge';
import { StorageInterface } from './childNavStores';

// Content record with metadata
export interface ContentRecord {
  id: string;
  contentType: string;
  contentTypeId: string;
  languageTypeId: string;
  tableData: TableData;
  quickAccess: QuickAccessTable;
  lastUpdated: string;
  lastSynced: string | null;
  syncVersion: string | null;
  isOutdated: boolean;
}

// Content query parameters
export interface ContentQuery {
  contentType: string;
  contentTypeId: string;
  languageTypeId: string;
}

// Content store state interface
export interface ContentStoreState {
  // Content records storage
  records: ContentRecord[];
  
  // Loading and sync state
  isLoading: boolean;
  isSyncing: boolean;
  syncError: string | null;
  lastGlobalSync: string | null;
  
  // Actions for content management
  addOrUpdateContent: (query: ContentQuery, tableData: TableData) => void;
  getContent: (query: ContentQuery) => ContentRecord | null;
  removeContent: (query: ContentQuery) => void;
  hasContent: (query: ContentQuery) => boolean;
  isContentOutdated: (query: ContentQuery, maxAgeMinutes?: number) => boolean;
  
  // Bulk operations
  getContentsByType: (contentType: string) => ContentRecord[];
  getContentsByLanguage: (languageTypeId: string) => ContentRecord[];
  removeContentsByType: (contentType: string) => void;
  clearAllContent: () => void;
  
  // Sync state management
  setLoading: (loading: boolean) => void;
  setSyncing: (syncing: boolean) => void;
  setSyncError: (error: string | null) => void;
  markGlobalSyncComplete: () => void;
  
  // Utility actions
  getContentStats: () => {
    totalRecords: number;
    recordsByType: Record<string, number>;
    recordsByLanguage: Record<string, number>;
    outdatedRecords: number;
    lastSyncStatus: string;
  };
  
  // Content table field extraction
  getContentTableFields: (record: ContentRecord) => {
    content_type: string | null;
    content_title: string | null;
    content_type_id: string | null;
    content_languagetype_id: string | null;
    modified_date: string | null;
  } | null;
  
  getRecordsWithContentFields: () => Array<ContentRecord & {
    contentFields: {
      content_type: string | null;
      content_title: string | null;
      content_type_id: string | null;
      content_languagetype_id: string | null;
      modified_date: string | null;
    } | null;
  }>;
  
  reset: () => void;
}

// AdminSegments are stored once per segment — never per language. A download built for one
// language carries English first plus that language's text, and is merged into the existing
// record (see utils/adminSegmentMerge), so selecting a language never means loading a different
// AdminSegment. Every other content type stays keyed by language.
const ADMIN_SEGMENT = 'AdminSegment';

const makeRecordId = (contentType: string, contentTypeId: string, languageTypeId: string): string =>
  contentType === ADMIN_SEGMENT
    ? `${contentType}_${contentTypeId}`
    : `${contentType}_${contentTypeId}_${languageTypeId}`;

const mergeAdminSegmentRecords = (existing: ContentRecord, incoming: ContentRecord): ContentRecord => {
  const tableData = mergeAdminSegmentTableData(existing.tableData as any, incoming.tableData as any) as TableData;
  let quickAccess = incoming.quickAccess;
  try {
    quickAccess = toMemoryOptimized(tableData);
  } catch {
    // AdminSegment tables aren't always in the shape QuickAccessTable needs — keep what we have.
  }
  return {
    ...incoming,
    id: makeRecordId(ADMIN_SEGMENT, incoming.contentTypeId, ''),
    languageTypeId: '1', // English is the base language of the merged record
    tableData,
    quickAccess,
  };
};

/**
 * Collapses legacy per-language AdminSegment records (AdminSegment_<id>_<lang>) persisted by
 * earlier versions into one record per segment. English folds in first so other languages'
 * text is layered on top of it.
 */
export const collapseAdminSegmentRecords = (records: ContentRecord[]): ContentRecord[] => {
  const collapsed: ContentRecord[] = [];
  const indexBySegment = new Map<string, number>();
  const ordered = [
    ...records.filter(r => r.contentType !== ADMIN_SEGMENT),
    ...records.filter(r => r.contentType === ADMIN_SEGMENT && r.languageTypeId === '1'),
    ...records.filter(r => r.contentType === ADMIN_SEGMENT && r.languageTypeId !== '1'),
  ];
  for (const record of ordered) {
    if (record.contentType !== ADMIN_SEGMENT) {
      collapsed.push(record);
      continue;
    }
    const existingIndex = indexBySegment.get(record.contentTypeId);
    if (existingIndex === undefined) {
      indexBySegment.set(record.contentTypeId, collapsed.length);
      collapsed.push({ ...record, id: makeRecordId(ADMIN_SEGMENT, record.contentTypeId, ''), languageTypeId: '1' });
    } else {
      collapsed[existingIndex] = mergeAdminSegmentRecords(collapsed[existingIndex], record);
    }
  }
  return collapsed;
};

// Content store factory function
export const createContentStore = (storage: StorageInterface) => {
  const zustandStorage = createJSONStorage(() => storage);
  
  return create<ContentStoreState>()(
    persist(
      (set, get) => ({
        // Initial state
        records: [],
        isLoading: false,
        isSyncing: false,
        syncError: null,
        lastGlobalSync: null,
        
        // Content management actions
        addOrUpdateContent: (query, tableData) => {
          const { contentType, contentTypeId, languageTypeId } = query;
          const id = makeRecordId(contentType, contentTypeId, languageTypeId);
          const now = new Date().toISOString();

          console.log(`🔍 ContentStore.addOrUpdateContent called:`, {
            id,
            contentType,
            contentTypeId,
            languageTypeId,
            tableName: tableData?.Name,
            hasData: !!(tableData?.Data),
            rowCount: tableData?.Data?.Rows?.length || 0,
            columnCount: tableData?.Data?.Columns?.length || 0
          });
          
          set((state) => {
            console.log(`📊 ContentStore current state: ${state.records.length} records`);
            const existingIndex = state.records.findIndex(r => r.id === id);
            console.log(`🔍 Existing record index for ${id}: ${existingIndex}`);
            
            const quickAccess = toMemoryOptimized(tableData);
            
            const newRecord: ContentRecord = {
              id,
              contentType,
              contentTypeId,
              languageTypeId,
              tableData,
              quickAccess,
              lastUpdated: now,
              lastSynced: now,
              syncVersion: tableData.Name || '1.0', // Use table name as version or fallback
              isOutdated: false,
            };
            
            if (existingIndex >= 0) {
              // Update existing record
              console.log(`🔄 Updating existing record at index ${existingIndex} for ${id}`);
              const updatedRecords = [...state.records];
              updatedRecords[existingIndex] = contentType === ADMIN_SEGMENT
                ? mergeAdminSegmentRecords(state.records[existingIndex], newRecord)
                : newRecord;
              console.log(`📈 Updated records count: ${updatedRecords.length}`);
              return { records: updatedRecords };
            } else {
              // Add new record
              console.log(`➕ Adding new record for ${id}`);
              const newRecords = [
                ...state.records,
                // English is the base language of a merged AdminSegment record
                contentType === ADMIN_SEGMENT ? { ...newRecord, languageTypeId: '1' } : newRecord,
              ];
              console.log(`📈 New records count: ${newRecords.length}`);
              return { records: newRecords };
            }
          });
        },
        
        getContent: (query) => {
          const { contentType, contentTypeId, languageTypeId } = query;
          const id = makeRecordId(contentType, contentTypeId, languageTypeId);
          const state = get();
          return state.records.find(r => r.id === id) || null;
        },
        
        removeContent: (query) => {
          const { contentType, contentTypeId, languageTypeId } = query;
          const id = makeRecordId(contentType, contentTypeId, languageTypeId);
          set((state) => ({
            records: state.records.filter(r => r.id !== id)
          }));
        },
        
        hasContent: (query) => {
          const content = get().getContent(query);
          return content !== null;
        },
        
        isContentOutdated: (query, maxAgeMinutes = 60) => {
          const content = get().getContent(query);
          if (!content) return true;
          
          const now = new Date().getTime();
          const lastUpdated = new Date(content.lastUpdated).getTime();
          const ageInMinutes = (now - lastUpdated) / (1000 * 60);
          
          return ageInMinutes > maxAgeMinutes || content.isOutdated;
        },
        
        // Bulk operations
        getContentsByType: (contentType) => {
          const state = get();
          return state.records.filter(r => r.contentType === contentType);
        },
        
        getContentsByLanguage: (languageTypeId) => {
          const state = get();
          return state.records.filter(r => r.languageTypeId === languageTypeId);
        },
        
        removeContentsByType: (contentType) => {
          set((state) => ({
            records: state.records.filter(r => r.contentType !== contentType)
          }));
        },
        
        clearAllContent: () => {
          set({ records: [] });
        },
        
        // Sync state management
        setLoading: (loading) => set({ isLoading: loading }),
        setSyncing: (syncing) => set({ isSyncing: syncing }),
        setSyncError: (error) => set({ syncError: error }),
        
        markGlobalSyncComplete: () => {
          const now = new Date().toISOString();
          set({ lastGlobalSync: now, syncError: null });
        },
        
        // Utility actions
        getContentStats: () => {
          const state = get();
          const recordsByType: Record<string, number> = {};
          const recordsByLanguage: Record<string, number> = {};
          let outdatedRecords = 0;
          
          state.records.forEach(record => {
            // Count by type
            recordsByType[record.contentType] = (recordsByType[record.contentType] || 0) + 1;
            
            // Count by language
            recordsByLanguage[record.languageTypeId] = (recordsByLanguage[record.languageTypeId] || 0) + 1;
            
            // Count outdated
            if (record.isOutdated) outdatedRecords++;
          });
          
          return {
            totalRecords: state.records.length,
            recordsByType,
            recordsByLanguage,
            outdatedRecords,
            lastSyncStatus: state.syncError || (state.lastGlobalSync ? 'Success' : 'Never synced'),
          };
        },
        
        reset: () => {
          set({
            records: [],
            isLoading: false,
            isSyncing: false,
            syncError: null,
            lastGlobalSync: null,
          });
        },
        
        // Helper methods for accessing content table fields from TableData
        getContentTableFields: (record: ContentRecord) => {
          console.log(`🔍 getContentTableFields called for record: ${record.id}`);
          const tableData = record.tableData;
          
          if (!tableData) {
            console.log(`❌ No tableData found for record: ${record.id}`);
            return null;
          }
          
          console.log(`📊 Table data structure for ${record.id}:`, {
            hasData: !!(tableData as any).Data,
            hasTables: !!((tableData as any).Tables),
            tableName: tableData.Name,
            isArray: Array.isArray((tableData as any).Tables),
            columns: (tableData as any).Data?.Columns || 'none',
            rowCount: (tableData as any).Data?.Rows?.length || 0
          });
          
          // Handle case where tableData might be a TablesDto with multiple tables
          const tables: any[] = [];
          
          if ((tableData as any).Tables && Array.isArray((tableData as any).Tables)) {
            // It's a TablesDto with multiple tables
            console.log(`📚 Found TablesDto with ${(tableData as any).Tables.length} tables`);
            tables.push(...(tableData as any).Tables);
          } else if (tableData.Data?.Rows && tableData.Data?.Columns) {
            // It's a single TableData object
            console.log(`📝 Found single TableData object`);
            tables.push(tableData);
          }
          
          console.log(`🔍 Processing ${tables.length} tables for ${record.id}`);
          
          // Look for the content table
          let contentTable = null;
          for (let i = 0; i < tables.length; i++) {
            const table = tables[i];
            console.log(`🔍 Checking table ${i}:`, {
              name: table?.Name,
              hasData: !!(table?.Data),
              hasRows: !!(table?.Data?.Rows),
              hasColumns: !!(table?.Data?.Columns),
              columns: table?.Data?.Columns || 'none'
            });
            
            if (!table?.Data?.Rows || !table?.Data?.Columns) {
              console.log(`⚠️ Table ${i} skipped - missing data structure`);
              continue;
            }
            
            // Check if this is the content table by name or if it contains content fields
            const isContentTable = table.Name === 'content' || 
              table.Data.Columns.includes('content_title') ||
              table.Data.Columns.includes('content_type');
              
            console.log(`🎯 Table ${i} content check:`, {
              name: table.Name,
              nameMatch: table.Name === 'content',
              hasTitleColumn: table.Data.Columns.includes('content_title'),
              hasTypeColumn: table.Data.Columns.includes('content_type'),
              isContentTable,
              allColumns: table.Data.Columns
            });
            
            if (isContentTable) {
              contentTable = table;
              console.log(`✅ Found content table: ${table.Name}`);
              break;
            }
          }
          
          if (!contentTable) {
            console.log(`❌ No content table found for record: ${record.id}`);
            console.log(`Available tables:`, tables.map(t => ({ name: t?.Name, columns: t?.Data?.Columns })));
            return null;
          }
          
          const columns = contentTable.Data.Columns;
          const firstRow = contentTable.Data.Rows[0];
          
          if (!firstRow) {
            console.log(`❌ No rows found in content table for record: ${record.id}`);
            return null;
          }
          
          // Extract the requested fields
          const getFieldValue = (fieldName: string) => {
            const index = columns.indexOf(fieldName);
            return index >= 0 ? firstRow[index] : null;
          };
          
          const contentFields = {
            content_type: getFieldValue('content_type'),
            content_title: getFieldValue('content_title'),
            content_type_id: getFieldValue('content_type_id'),
            content_languagetype_id: getFieldValue('content_languagetype_id'),
            modified_date: getFieldValue('modified_date')
          };
          
          console.log(`✅ Extracted content fields for ${record.id}:`, contentFields);
          return contentFields;
        },
        
        // Get all records with extracted content table fields for display
        getRecordsWithContentFields: () => {
          const state = get();
          console.log(`🔍 getRecordsWithContentFields: Processing ${state.records.length} records`);
          
          const recordsWithFields = state.records.map((record, index) => {
            console.log(`🔍 Processing record ${index}: ${record.id}`);
            const contentFields = state.getContentTableFields(record);
            console.log(`📊 Content fields for ${record.id}:`, contentFields);
            
            return {
              ...record,
              contentFields
            };
          });
          
          console.log(`✅ getRecordsWithContentFields returning ${recordsWithFields.length} records with fields`);
          return recordsWithFields;
        },
      }),
      {
        name: 'child-nav-content',
        storage: zustandStorage,
        // Collapse legacy per-language AdminSegment records into one per segment on load.
        merge: (persistedState, currentState) => {
          const persisted = (persistedState ?? {}) as Partial<ContentStoreState>;
          return {
            ...currentState,
            ...persisted,
            records: Array.isArray(persisted.records)
              ? collapseAdminSegmentRecords(persisted.records)
              : currentState.records,
          };
        },
      }
    )
  );
};

// Helper functions for content queries
export const createContentQuery = (
  contentType: string,
  contentTypeId: string,
  languageTypeId: string
): ContentQuery => ({
  contentType,
  contentTypeId,
  languageTypeId,
});

// Content type constants (based on backend usage patterns)
export const CONTENT_TYPES = {
  ADMIN_SEGMENT: 'AdminSegment',
  APP_LABELS: 'AppLabels',
  ACTIVITIES: 'Activities',
  GUIDANCE: 'Guidance',
} as const;

// Language type constants
export const LANGUAGE_TYPES = {
  ENGLISH: '1',
  SPANISH: '2',
  FRENCH: '3',
  GERMAN: '4',
  PORTUGUESE: '9',
  CHINESE: '14',
} as const;

export type ContentType = typeof CONTENT_TYPES[keyof typeof CONTENT_TYPES];
export type LanguageType = typeof LANGUAGE_TYPES[keyof typeof LANGUAGE_TYPES];